import React, { FunctionComponent, useLayoutEffect, useMemo, useRef } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap } from 'three';
import { transitionAdjustedRenderOrder } from '../../utils';
import { IEntityActionState, useActionStateStore, useDataStore, useSceneStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IModel3d, IModel3dAnimationState, IModel3dReactProps } from '../r3f-components/component-data-structure';
import { EntityErrorBoundary } from '../r3f-components/components/boundaries';
import { ITransitionPhase } from '../r3f-components/transitions/spec';
import { useGetAdjustedMemoSpatialProps, useGetSubscribedSceneTransitionCategoryRef, useGetSubscribedSceneTransitionPhaseRef, useHocPointerDown, useOnEntityTestClick, useResetEntityTransformsOnPlacement } from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { hasTapAction } from './hocUtils';
import { Jute } from '../..';
import { filestore, FsModel3D } from '@/filestore';

interface IModel3dWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}


const withModel3dBehaviour = (WrappedModel3d: FunctionComponent<IModel3dReactProps>): FunctionComponent<IModel3dWrapped> => {
	const FuncComp: FunctionComponent<IModel3dWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const model3d = useDataStore(state => state.componentsById?.[id] as IModel3d) ?? {};
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled) ?? true;
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const isPlacementMode = useSceneStore(({ placementMode }) => placementMode);
		const transitionPhase = useSceneStore((state) => state.transitionPhase[sceneId]);

		useOnEntityTestClick({ sceneId, entity: model3d, sceneTransCatRef, sceneTransPhaseRef });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (model3d?.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(model3d, screenRelative);
		const { opacity, filestoreId, idleAnimation, idlePoseTime, originalBBox, castShadow = false, receiveShadow = false } = model3d;

		const onPointerDown = useHocPointerDown({ pointerIsDownRef, sceneTransCatRef, sceneTransPhaseRef, sceneId, entity: model3d, screenRelative });

		const model3dUrl = filestore.get<FsModel3D>(filestoreId).getModelUrl();

		// Model animation specific behaviour
		const rawActionState = useActionStateStore(state => state.entityActionStateDict?.[id]);

		const animationState = useMemo(() => {
			return Object.keys(rawActionState || {}).reduce((acc, actionCategory) => {
				return { ...acc, ...rawActionState?.[actionCategory as keyof IEntityActionState] };
			}, {} as IModel3dAnimationState);
		}, [rawActionState]);

		useInteraction(id, 'onSelect', e => {
			onPointerDown(e.intersections)
			pointerIsDownRef.current = false;
		}, hasTapAction(model3d))

		useLayoutEffect(() => {
			const createInitialStaticGeometryIfNoIdleState = () => {
				if (!idleAnimation && typeof idlePoseTime === 'undefined') {
					Jute.staticGeometryWorkerController.generateStaticGeometry(model3dUrl, id)
				};
			}

			if (Jute.isContentLoaded) createInitialStaticGeometryIfNoIdleState()
			else Jute.onContentLoaded(createInitialStaticGeometryIfNoIdleState)
		}, [])

		const isPlayingAnimation = isPlacementMode
			? false
			: transitionPhase === ITransitionPhase.enterFinish && (animationState.isPlayingAnimation || (!!model3d.idleAnimation && typeof model3d.idlePoseTime === 'undefined'));

		return (
			<group name={`transformGroup-${id}`} position={position} >
				<group rotation={rotation}>
					<group name={`transformGroup-inner_${id}`} >
						<EntityLevelFadeGroup name={`fadeGroup-${id}`} sceneId={sceneId} position={zeroTuple3} entityId={id} screenRelative={screenRelative || false}>
							<EntityErrorBoundary renderOrder={renderOrder} scale={[1, 1, 1]} rotation={zeroTuple3}>
								<group name={`toggleVisibilityGroup-${id}`} >
									<group ref={pointerGroupRef} onPointerUp={() => (pointerIsDownRef.current = false)}>
										<WrappedModel3d
											id={id}
											renderOrder={renderOrder}
											rotation={zeroTuple3}
											animationName={animationState.animationName}
											isPlayingAnimation={isPlayingAnimation}
											animationRepetitions={animationState.animationRepetitions}
											animationRunCounter={animationState.animationRunCounter}
											isClampWhenFinished={animationState.isClampWhenFinished}
											idleAnimation={idleAnimation}
											idlePoseTime={idlePoseTime}
											model3dUrl={model3dUrl}
											scale={scale}
											originalBBox={originalBBox}
											position={zeroTuple3}
											castShadow={shadowsEnabled && castShadow}
											receiveShadow={shadowsEnabled && receiveShadow}
											opacity={opacity}
											raycast={raycast}
											sceneId={sceneId}
											enableBVH={hasTapAction(model3d)}
											onPointerDown={e => onPointerDown(e.intersections)}
											staticGeometryWorkerController={Jute.staticGeometryWorkerController}
											bvhWorkerController={Jute.bvhWorkerController}
										/>
									</group>
								</group>
							</EntityErrorBoundary>
						</EntityLevelFadeGroup>
					</group>
				</group>
			</group>
		);
	};
	return FuncComp;
};

export default withModel3dBehaviour;
